import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError, fmtDate } from "../lib/api";
import { Plus, Trash2, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

const ROLE_OPTS = [
    { key: "RECEPTIONIST", label: "Receptionist" },
    { key: "HOUSEKEEPER", label: "Housekeeper" },
    { key: "SECURITY", label: "Security Guard" },
    { key: "OTHER", label: "Other" },
];

export default function RosterPage() {
    const { siteId } = useParams();
    const [site, setSite] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ name: "", role: "RECEPTIONIST", active_from: new Date().toISOString().slice(0, 10) });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, r] = await Promise.all([api.get(`/sites/${siteId}`), api.get(`/sites/${siteId}/roster`)]);
            setSite(s.data);
            setRows(r.data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, [siteId]);

    useEffect(() => { load(); }, [load]);

    async function add(e) {
        e.preventDefault();
        try {
            await api.post(`/sites/${siteId}/roster`, form);
            toast.success("Staff added");
            setForm({ name: "", role: "RECEPTIONIST", active_from: new Date().toISOString().slice(0, 10) });
            load();
        } catch (e) { toast.error(apiError(e)); }
    }

    async function endDate(id, dateVal) {
        try {
            await api.patch(`/sites/${siteId}/roster/${id}`, { active_to: dateVal });
            toast.success("End date set");
            load();
        } catch (e) { toast.error(apiError(e)); }
    }

    async function updateName(id, name) {
        try { await api.patch(`/sites/${siteId}/roster/${id}`, { name }); }
        catch (e) { toast.error(apiError(e)); }
    }

    return (
        <div className="mx-auto max-w-4xl px-4 sm:px-6 py-6 sm:py-10">
            <Link to={`/sites/${siteId}/attendance`} className="text-xs text-gray-500 hover:text-[#1E3355] inline-flex items-center gap-1 mb-3" data-testid="roster-back">
                <ChevronLeft className="w-3 h-3" /> Back to attendance
            </Link>
            <div>
                <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Staff Roster</div>
                <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">{site?.name || "…"}</h1>
                <p className="text-sm text-gray-500 mt-1">Autosaves on edit. End-date a staff member instead of deleting to keep history.</p>
            </div>

            <form onSubmit={add} className="mt-6 rounded-xl bg-white border border-gray-100 fx-shadow-card p-4 sm:p-5" data-testid="roster-add-form">
                <div className="font-display font-semibold text-[#1E3355] mb-3">Add staff</div>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" required className={fieldCls + " sm:col-span-2"} data-testid="roster-add-name" />
                    <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={fieldCls} data-testid="roster-add-role">
                        {ROLE_OPTS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                    </select>
                    <input type="date" value={form.active_from} onChange={(e) => setForm({ ...form, active_from: e.target.value })} required className={fieldCls} data-testid="roster-add-from" />
                </div>
                <button type="submit" className="mt-3 inline-flex items-center gap-1 bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold px-4 py-2 rounded-md" data-testid="roster-add-submit">
                    <Plus className="w-4 h-4" /> Add
                </button>
            </form>

            <div className="mt-6 rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-hidden">
                <table className="w-full text-sm" data-testid="roster-table">
                    <thead className="bg-[#F4F6F9]">
                        <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                            <th className="px-4 py-3">Name</th>
                            <th className="px-4 py-3">Role</th>
                            <th className="px-4 py-3">Active from</th>
                            <th className="px-4 py-3">End date</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {loading ? <tr><td colSpan="4" className="p-6 text-center text-sm text-gray-500">Loading…</td></tr> :
                        rows.length === 0 ? <tr><td colSpan="4" className="p-6 text-center text-sm text-gray-500" data-testid="roster-empty">No staff yet.</td></tr> :
                        rows.map((r) => (
                            <tr key={r.id} data-testid={`roster-row-${r.name}`}>
                                <td className="px-4 py-3"><input defaultValue={r.name} onBlur={(e) => e.target.value !== r.name && updateName(r.id, e.target.value)} className="rounded border border-transparent hover:border-gray-200 px-2 py-1 text-sm font-medium text-[#1E3355] focus:border-[#1E3355] focus:outline-none focus:ring-1 focus:ring-[#1E3355] bg-transparent" /></td>
                                <td className="px-4 py-3 text-gray-600 text-xs">{ROLE_OPTS.find((o) => o.key === r.role)?.label}</td>
                                <td className="px-4 py-3 text-xs text-gray-500">{fmtDate(r.active_from)}</td>
                                <td className="px-4 py-3 text-xs">
                                    <input type="date" defaultValue={r.active_to || ""} onBlur={(e) => endDate(r.id, e.target.value || null)} className={fieldCls + " max-w-[160px]"} data-testid={`roster-end-${r.name}`} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

const fieldCls = "rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1E3355] focus:ring-1 focus:ring-[#1E3355] bg-white";
